// Campañas de tarjetas de sellos (catálogo). POS → Configuración → Sellos.
// "Junta N sellos → premio". El sello se otorga por comprar un producto o un
// producto de una categoría; al completar la meta se genera un cupón de premio
// (producto gratis / saldo a monedero / puntos / % descuento).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Stamp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, CategoriaOpt, Modal, PREMIOS_SELLO, ProductoMin, SelloCampana, Switch, categoriasList, fieldCls, pos, productosMinList } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

const vacio = {
  codigo: '', nombre: '', objetivo: 'producto', producto_id: '', categoria_id: '',
  monto_por_sello: 10, monto_minimo: 0,
  sellos_por_unidad: 1, sellos_meta: 10, caducidad_meses: 12,
  premio_tipo: 'producto', premio_producto_id: '', premio_cantidad: 1,
  premio_monto: 0, premio_puntos: 0, premio_descuento_pct: 0, premio_caducidad_meses: 3,
  vigente_desde: '', vigente_hasta: '', es_activo: true,
};
type Form = typeof vacio;
const premioLabel = (t: string) => PREMIOS_SELLO.find((x) => x.v === t)?.label ?? t;

export function Sellos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<SelloCampana[]>({ queryKey: ['pos-sellos-camp'], queryFn: () => pos.list<SelloCampana>('sellos_campanas', '?select=*&order=nombre') });
  const { data: productos = [] } = useQuery<ProductoMin[]>({ queryKey: ['pos-prod-min'], queryFn: productosMinList });
  const { data: categorias = [] } = useQuery<CategoriaOpt[]>({ queryKey: ['inv-cat-min'], queryFn: categoriasList });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: SelloCampana) => {
    if (c) {
      setEditId(c.id);
      setForm({
        codigo: c.codigo ?? '', nombre: c.nombre,
        objetivo: c.modo === 'visita' || c.modo === 'monto' ? c.modo : (c.categoria_id ? 'categoria' : 'producto'),
        producto_id: c.producto_id ?? '', categoria_id: c.categoria_id ?? '',
        monto_por_sello: Number(c.monto_por_sello ?? 10), monto_minimo: Number(c.monto_minimo ?? 0),
        sellos_por_unidad: Number(c.sellos_por_unidad), sellos_meta: c.sellos_meta, caducidad_meses: c.caducidad_meses,
        premio_tipo: c.premio_tipo, premio_producto_id: c.premio_producto_id ?? '', premio_cantidad: Number(c.premio_cantidad),
        premio_monto: Number(c.premio_monto), premio_puntos: Number(c.premio_puntos), premio_descuento_pct: Number(c.premio_descuento_pct),
        premio_caducidad_meses: c.premio_caducidad_meses, vigente_desde: c.vigente_desde ?? '', vigente_hasta: c.vigente_hasta ?? '', es_activo: c.es_activo,
      });
    } else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const t = form.premio_tipo;
      const modo = form.objetivo === 'visita' || form.objetivo === 'monto' ? form.objetivo : 'producto';
      const body = {
        codigo: form.codigo || null, nombre: form.nombre,
        modo,
        monto_por_sello: modo === 'monto' ? (Number(form.monto_por_sello) || 0) : null,
        monto_minimo: modo === 'visita' ? (Number(form.monto_minimo) || 0) : 0,
        producto_id: form.objetivo === 'producto' ? (form.producto_id || null) : null,
        categoria_id: form.objetivo === 'categoria' ? (form.categoria_id || null) : null,
        sellos_por_unidad: Number(form.sellos_por_unidad) || 1, sellos_meta: Number(form.sellos_meta) || 1,
        caducidad_meses: Number(form.caducidad_meses) || 0,
        premio_tipo: t,
        premio_producto_id: t === 'producto' ? (form.premio_producto_id || null) : null,
        premio_cantidad: t === 'producto' ? (Number(form.premio_cantidad) || 1) : 1,
        premio_monto: t === 'monedero' ? Number(form.premio_monto) : 0,
        premio_puntos: t === 'puntos' ? Number(form.premio_puntos) : 0,
        premio_descuento_pct: t === 'descuento' ? Number(form.premio_descuento_pct) : 0,
        premio_caducidad_meses: Number(form.premio_caducidad_meses) || 0,
        vigente_desde: form.vigente_desde || null, vigente_hasta: form.vigente_hasta || null, es_activo: form.es_activo,
      };
      if (form.objetivo === 'producto' && !body.producto_id) throw new Error('Selecciona el producto que otorga el sello');
      if (form.objetivo === 'categoria' && !body.categoria_id) throw new Error('Selecciona la categoría que otorga el sello');
      if (form.objetivo === 'monto' && !body.monto_por_sello) throw new Error('Indica cada cuántos dólares de consumo se gana un sello');
      if (t === 'producto' && !body.premio_producto_id) throw new Error('Selecciona el producto de premio');
      if (editId) await pos.update('sellos_campanas', editId, body); else await pos.create('sellos_campanas', body);
    },
    onSuccess: () => { toast.success('Campaña guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-sellos-camp'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => pos.remove('sellos_campanas', id),
    onSuccess: () => { toast.success('Campaña eliminada'); void qc.invalidateQueries({ queryKey: ['pos-sellos-camp'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const objetivoTxt = (c: SelloCampana) =>
    c.modo === 'visita' ? `Cada visita${Number(c.monto_minimo) > 0 ? ` (consumo mín. $${Number(c.monto_minimo).toFixed(2)})` : ''}`
    : c.modo === 'monto' ? `Cada $${Number(c.monto_por_sello ?? 0).toFixed(2)} de consumo`
    : c.categoria_id ? `Categoría: ${categorias.find((x) => x.id === c.categoria_id)?.nombre ?? '—'}`
    : `Producto: ${productos.find((x) => x.id === c.producto_id)?.nombre ?? '—'}`;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Stamp className="w-5 h-5" /> Tarjetas de sellos</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>
      <p className="text-xs text-muted-foreground">Junta sellos por producto/categoría, por VISITA (cada cuenta cobrada) o por MONTO consumido; al completar la meta el cliente gana un premio. El canje se realiza en el Punto de Venta. El Consumidor Final no acumula.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Campañas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Otorga sello por</th><th className="py-2 pr-3">Meta</th><th className="py-2 pr-3">Premio</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin campañas</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">{objetivoTxt(c)}</td>
                  <td className="py-2 pr-3">{c.sellos_meta} sellos</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{premioLabel(c.premio_tipo)}</span></td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar campaña' : 'Nueva campaña'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            </div>

            <div className="rounded-md border p-3 space-y-3">
              <Campo label="Otorga sello por">
                <select className={fieldCls} value={form.objetivo} onChange={(e) => set({ objetivo: e.target.value })}>
                  <option value="producto">Un producto específico</option>
                  <option value="categoria">Cualquier producto de una categoría</option>
                  <option value="visita">Cada visita (cuenta cobrada) — restaurante</option>
                  <option value="monto">Cada $X de consumo</option>
                </select>
              </Campo>
              {form.objetivo === 'producto' && (
                <Campo label="Producto">
                  <select className={fieldCls} value={form.producto_id} onChange={(e) => set({ producto_id: e.target.value })}>
                    <option value="">— Selecciona —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                  </select>
                </Campo>
              )}
              {form.objetivo === 'categoria' && (
                <Campo label="Categoría">
                  <select className={fieldCls} value={form.categoria_id} onChange={(e) => set({ categoria_id: e.target.value })}>
                    <option value="">— Selecciona —</option>{categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </Campo>
              )}
              {form.objetivo === 'visita' && (
                <Campo label="Consumo mínimo de la cuenta ($, 0 = sin mínimo)">
                  <input type="number" min="0" step="0.01" className={fieldCls} value={form.monto_minimo} onChange={(e) => set({ monto_minimo: Number(e.target.value) })} />
                </Campo>
              )}
              {form.objetivo === 'monto' && (
                <Campo label="Dólares de consumo por sello ($)">
                  <input type="number" min="0.01" step="0.01" className={fieldCls} value={form.monto_por_sello} onChange={(e) => set({ monto_por_sello: Number(e.target.value) })} />
                </Campo>
              )}
              <div className="grid grid-cols-3 gap-3">
                {(form.objetivo === 'producto' || form.objetivo === 'categoria') && (
                  <Campo label="Sellos por unidad"><input type="number" min="0.01" step="0.01" className={fieldCls} value={form.sellos_por_unidad} onChange={(e) => set({ sellos_por_unidad: Number(e.target.value) })} /></Campo>
                )}
                <Campo label="Meta (sellos)"><input type="number" min="1" className={fieldCls} value={form.sellos_meta} onChange={(e) => set({ sellos_meta: Number(e.target.value) })} /></Campo>
                <Campo label="Caducidad sellos (meses, 0=no)"><input type="number" min="0" className={fieldCls} value={form.caducidad_meses} onChange={(e) => set({ caducidad_meses: Number(e.target.value) })} /></Campo>
              </div>
            </div>

            <div className="rounded-md border p-3 space-y-3">
              <Campo label="Premio al completar la tarjeta">
                <select className={fieldCls} value={form.premio_tipo} onChange={(e) => set({ premio_tipo: e.target.value })}>
                  {PREMIOS_SELLO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Campo>
              {form.premio_tipo === 'producto' && (
                <div className="grid grid-cols-3 gap-3">
                  <Campo label="Producto de premio" className="col-span-2">
                    <select className={fieldCls} value={form.premio_producto_id} onChange={(e) => set({ premio_producto_id: e.target.value })}>
                      <option value="">— Selecciona —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Cantidad"><input type="number" min="1" step="0.01" className={fieldCls} value={form.premio_cantidad} onChange={(e) => set({ premio_cantidad: Number(e.target.value) })} /></Campo>
                </div>
              )}
              {form.premio_tipo === 'monedero' && (
                <Campo label="Monto a monedero ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={form.premio_monto} onChange={(e) => set({ premio_monto: Number(e.target.value) })} /></Campo>
              )}
              {form.premio_tipo === 'puntos' && (
                <Campo label="Puntos a otorgar"><input type="number" min="0" step="0.0001" className={fieldCls} value={form.premio_puntos} onChange={(e) => set({ premio_puntos: Number(e.target.value) })} /></Campo>
              )}
              {form.premio_tipo === 'descuento' && (
                <Campo label="Descuento (%)"><input type="number" min="0" max="100" step="0.001" className={fieldCls} value={form.premio_descuento_pct} onChange={(e) => set({ premio_descuento_pct: Number(e.target.value) })} /></Campo>
              )}
              <Campo label="Caducidad del premio (meses, 0=no)"><input type="number" min="0" className={`${fieldCls} w-48`} value={form.premio_caducidad_meses} onChange={(e) => set({ premio_caducidad_meses: Number(e.target.value) })} /></Campo>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Campo label="Vigente desde"><input type="date" className={fieldCls} value={form.vigente_desde} onChange={(e) => set({ vigente_desde: e.target.value })} /></Campo>
              <Campo label="Vigente hasta"><input type="date" className={fieldCls} value={form.vigente_hasta} onChange={(e) => set({ vigente_hasta: e.target.value })} /></Campo>
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activa" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
